import delve from 'dlv'
import { FC, useEffect, useState } from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { LAYERS } from '../lib/constants'

// components
import { ScanFooter, ScanHeader } from '../ScanPage'

// api
import { EmailClaim, getEmailClaim, getTokensFor, TokenInfo } from '../api'

/* Assets */
import Transaction from '../components/Transaction'
import LoadingSpinner from '../images/loading.svg'
import RedeemForm from './RedeemForm'

const isValidCallbackUrl = (callback: string) => {
  try {
    new URL(callback)

    return true
  } catch (err) {
    return false
  }
}

const redirectToCallbackUrlIfExists = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const callbackUrl = queryParams.get('callback_url')

  if (callbackUrl && isValidCallbackUrl(callbackUrl)) {
    window.location.href = callbackUrl
  }
}

export const RedeemPage: FC<RouteComponentProps> = ({ match }) => {
  // react hooks
  const [claim, setClaim] = useState<EmailClaim | null>(null)
  const [tokens, setTokens] = useState<TokenInfo[]>([])
  const [tokenError, setTokenError] = useState<boolean>(false)
  const [ethereumAddress, setEthereumAddress] = useState<string>('')
  const [transactionRequestId, setTransactionRequestId] = useState<number | undefined>(undefined)

  // lib hooks
  const { addToast } = useToasts()

  // constants
  const uid = delve(match, 'params.uid')

  // effects
  useEffect(() => {
    getClaim()
  }, []) // eslint-disable-line
  useEffect(() => {
    if (claim) getTokens()
  }, [claim]) // eslint-disable-line

  // methods
  const getClaim = async () => {
    try {
      let _claim = await getEmailClaim(uid)
      setClaim(_claim)
    } catch (e) {
      setTokenError(true)
      console.log('Error getting claim')
      console.log(e)
    }
  }

  const getTokens = async () => {
    if (!claim) return
    try {
      const tokens = await getTokensFor(claim.email)
      setTokens(tokens)
    } catch (error) {
      // @ts-expect-error TODO: error is type unknown
      addToast(error.message, {
        appearance: 'error',
        autoDismiss: false,
      })
    }
  }

  const handleTransactionRequestChange = (transactionRequestId?: number) => {
    setTransactionRequestId(transactionRequestId)
  }

  const handleAddressChange = (address: string) => {
    setEthereumAddress(address)
  }

  let body = (
    <div className={'preloader'}>
      <img src={LoadingSpinner} alt={'Loading'} />
    </div>
  )

  if (tokenError) {
    body = (
      <div className={'token-error'}>
        <div>We couldn't validate the information received.</div>
        <div>The link could have expired or been already claimed.</div>
        <div>
          Check your POAPs <Link to={'/scan'}>here</Link> and request another email if needed.
        </div>
      </div>
    )
  }

  if (claim && !tokenError) {
    body = (
      <>
        <RedeemForm
          email={claim.email}
          uid={uid}
          onAddressSuccess={handleAddressChange}
          onTransactionRequestChange={handleTransactionRequestChange}
        />
        {transactionRequestId && ethereumAddress && (
          <Transaction
            transactionRequestId={transactionRequestId}
            layer={LAYERS.layer2}
            address={ethereumAddress}
            successCallback={redirectToCallbackUrlIfExists}
          />
        )}

        <div className="redeem-text-container">
          <div className="redeem-subtitle">POAPs to be claimed</div>
        </div>

        <div className="redeem-poaps-container events-logos">
          {tokens.length > 0 ? (
            tokens.map((token: TokenInfo, index) => (
              <div key={index} className="event-circle" data-aos="fade-up">
                {typeof token.event.image_url === 'string' && (
                  <img src={token.event.image_url} alt={token.event.name} />
                )}
              </div>
            ))
          ) : (
            <span>Loading Tokens</span>
          )}
        </div>
      </>
    )
  }

  return (
    <div className="landing">
      <ScanHeader sectionName="Claim" />
      <div className="redeem-content-container">{body}</div>
      <ScanFooter path="home" />
    </div>
  )
}
